












// Import vendors ----------------------------------------------------------------------------------
import { defineComponent, computed } from '@vue/composition-api';
// Import components -------------------------------------------------------------------------------
import ChartEvolutionContainer from '@/components/charts/ChartEvolutionContainer.vue';
// Import plugins ----------------------------------------------------------------------------------
import i18n from '@/plugins/i18n';
// Import utils - ----------------------------------------------------------------------------------
import { useChart, EThemeColor } from '@/utils/charts.utils';
import { EUnitKey } from '@/utils/parameter-units.utils';
import { useUnit } from '@/utils/unit.utils';
// Import types ------------------------------------------------------------------------------------
import type { TDataStandard } from '@/components/patient/PatientEvolution.vue';
// -------------------------------------------------------------------------------------------------

export default defineComponent({
  name: 'ChartEvolutionStrideLength',
  components: {
    ChartEvolutionContainer
  },
  props: {
    manifest: {
      type: Array
    },
    strideLength: {
      type: Object
    },
    norms: {
      type: Object
    },
    exportation: {
      type: Boolean
    }
  },
  setup(properties) {
    const {
      evolutionChartStructure,
      getSideData,
      getSideDataVariations,
      getSerieItemStructure,
      getNormsStructure
    } = useChart();
    const { isImperial } = useUnit().currentUnit();

    const title = 'commons.standards.stride-length';

    const strideLength: TDataStandard = computed(() => getSideData(properties.strideLength));

    const dataVariations = computed(() =>
      getSideDataVariations({
        left: strideLength.value?.left.values,
        right: strideLength.value?.right.values
      })
    );

    const valid = computed(() => !!strideLength.value);

    const series = computed(() => {
      let data = [
        // Left
        getSerieItemStructure(
          'stride_length_left',
          `${i18n.t('commons.standards.stride-length')} (${(i18n.t('commons.standards.left') as string).slice(
            0,
            1
          )})`,
          strideLength.value?.left.values,
          EThemeColor.Chart1
        ),
        getSerieItemStructure(
          'stride_length_left',
          undefined,
          strideLength.value?.left.ranges,
          EThemeColor.Chart1,
          true
        ),
        // Right
        getSerieItemStructure(
          'stride_length_right',
          `${i18n.t('commons.standards.stride-length')} (${(
            i18n.t('commons.standards.right') as string
          ).slice(0, 1)})`,
          strideLength.value?.right.values,
          EThemeColor.Chart2
        ),
        getSerieItemStructure(
          'stride_length_right',
          undefined,
          strideLength.value?.right.ranges,
          EThemeColor.Chart2,
          true
        )
      ];
      // Have norms
      if (properties.norms)
        data.push(
          getNormsStructure('stride_length_norms', properties.norms as { min: number[]; max: number[] })
        );

      return data;
    });

    const chartOptions = computed(() => {
      return {
        ...evolutionChartStructure(
          title,
          properties.manifest,
          isImperial.value ? EUnitKey.Inch : EUnitKey.Centimeter,
          1,
          { exportation: properties.exportation }
        ),
        series: series.value
      };
    });

    return {
      title,
      dataVariations,
      chartOptions,
      valid
    };
  }
});
